import { useState } from 'react';
import { CohortStepStatus, CohortStepType } from 'src/types/cohorts';
import { motion } from 'motion/react';
import { MaterialSymbol } from 'react-material-symbols';
import { CheckIcon } from '@medeloop/medeloop-components-library';
import { ActionButtons } from '../action-buttons';
import { StreamingMarkdown } from 'src/components/common/streaming-markdown';

export interface QueryValidationStepProps {
  step: {
    data: { type: string; payload: { content: string; format: string } }[];
    type: CohortStepType;
    status: CohortStepStatus;
  };
  isActive: boolean;
  isCompleted: boolean;
  onAccept: () => void;
  onEdit: (args: { step: CohortStepType; editedContent: string }) => void;
}

export const QueryValidationStep = ({
  step,
  isActive,
  isCompleted,
  onAccept,
  onEdit,
}: QueryValidationStepProps) => {
  const stepContent = step.data.find((content) => content.type === 'text');
  const [isEditing, setIsEditing] = useState(false);
  const [editedContent, setEditedContent] = useState(stepContent?.payload.content || '');

  const handleEdit = () => {
    setIsEditing(!isEditing);
    setEditedContent(stepContent?.payload.content ?? '');
  };

  const handleCloseEditPanel = () => {
    setIsEditing(false);
  };

  const handleEditAcceptClick = () => {
    if (onEdit && editedContent) {
      onEdit({ step: step.type, editedContent });
    }
    setIsEditing(false);
  };

  // Render edit panel if editing
  if (isEditing) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -10 }}
        transition={{ duration: 0.3 }}
        className="relative mt-4 border border-gray-700 rounded-2xl p-6 bg-gray-900"
      >
        <div className="mb-8">
          <div className="text-sm pb-4 text-gray-500 font-normal">
            Edit your query below. You can modify the text to better match your requirements.
          </div>

          <textarea
            className="w-full bg-gray-900 text-white border-none focus:ring-0 focus:outline-none resize-none overflow-hidden"
            value={editedContent}
            autoFocus={true}
            onKeyDown={(e: React.KeyboardEvent<HTMLTextAreaElement>) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleEditAcceptClick();
              }
            }}
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => {
              setEditedContent(e.target.value);
            }}
          />
        </div>

        <div className="absolute right-4 top-6">
          <button className="text-white" onClick={handleCloseEditPanel}>
            <MaterialSymbol icon="close" className="w-3 h-3" />
          </button>
        </div>

        <div className="absolute bottom-4 right-4 flex gap-4">
          <button
            className="bg-primary-background-default hover:bg-primary-background-hover text-black font-medium px-3 rounded-full text-sm h-fit py-2 border border-stroke-on-dark-subdued"
            onClick={handleEditAcceptClick}
          >
            <div className="flex flex-row items-center">
              <CheckIcon className="w-5 h-5 mr-1" />
              <span>Accept</span>
            </div>
          </button>
        </div>
      </motion.div>
    );
  }

  // Render step content
  return (
    <div className={`mb-6 `}>
      {/* Render content based on type */}
      {step.data.map((content, contentIndex) => (
        <div key={contentIndex}>
          {content.type === 'text' && (
            <div className="prose prose-invert max-w-none text-white">
              <StreamingMarkdown content={content.payload.content} duration={1.5} />
            </div>
          )}
        </div>
      ))}

      {/* Show action buttons when appropriate */}
      {isActive && isCompleted && <ActionButtons onAccept={onAccept} onEdit={handleEdit} />}
    </div>
  );
};
